import type {
    CapabilityBindingGetResponse,
    McpCapability,
    McpToolVisibility,
    TenantTool,
} from '@circuitry-ai/doc-data';
import {
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Select,
    SelectItem,
    Textarea,
} from '@nextui-org/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CtyButton } from '../button/CtyButton';

export interface McpToolEditorValues {
    description: string;
    visibility: McpToolVisibility;
    inputSchemaJson?: string;
}

export interface McpToolEditorModalProps {
    readonly isOpen: boolean;
    readonly defaultDescription: string;
    readonly defaultVisibility?: McpToolVisibility;
    readonly defaultInputSchemaJson?: string;
    readonly showInputSchema?: boolean;
    readonly descriptionHelpText?: string;
    readonly inputSchemaPlaceholder?: string;
    readonly isUpdateMode?: boolean;
    readonly isLoading?: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly onConfirm: (values: McpToolEditorValues) => void;
}

interface McpToolEditorStateOptions {
    capability?: McpCapability;
    capabilityBinding?: CapabilityBindingGetResponse | null;
    tenantTool?: TenantTool;
    getFallbackDescription: () => string;
    includeInputSchema?: boolean;
}

export function normalizeMcpToolVisibility(value?: string | null): McpToolVisibility {
    return String(value ?? '').trim().toLowerCase() === 'external' ? 'external' : 'internal';
}

function resolveInputSchemaJson(
    capability?: McpCapability,
    capabilityBinding?: CapabilityBindingGetResponse | null,
    includeInputSchema?: boolean,
): string {
    if (!includeInputSchema) return '';

    if (capability?.parameters?.length) {
        return JSON.stringify(capability.parameters, null, 2);
    }

    const bindingSchema = capabilityBinding?.input_schema;
    if (bindingSchema !== undefined && bindingSchema !== null) {
        return JSON.stringify(bindingSchema, null, 2);
    }

    return '';
}

export function useMcpToolEditorState({
    capability,
    capabilityBinding,
    tenantTool,
    getFallbackDescription,
    includeInputSchema = false,
}: McpToolEditorStateOptions) {
    const [isOpen, setIsOpen] = useState(false);
    const [isUpdateMode, setIsUpdateMode] = useState(false);
    const [enableAfterUpdate, setEnableAfterUpdate] = useState(false);
    const [defaults, setDefaults] = useState({
        description: '',
        visibility: 'internal' as McpToolVisibility,
        inputSchemaJson: '',
    });

    const open = (updateMode: boolean, enableAfterCapabilityUpdate = false) => {
        const resolvedCapability = capability ?? capabilityBinding?.capability;
        const hasRegisteredTool = Boolean(resolvedCapability || tenantTool?.capability_id);
        const effectiveUpdateMode = updateMode && hasRegisteredTool;

        let description = getFallbackDescription();
        let visibility: McpToolVisibility = 'internal';
        let inputSchemaJson = '';

        if (effectiveUpdateMode) {
            description =
                resolvedCapability?.description?.trim()
                || tenantTool?.description?.trim()
                || getFallbackDescription();
            visibility = normalizeMcpToolVisibility(
                resolvedCapability?.visibility ?? tenantTool?.visibility,
            );
            inputSchemaJson = resolveInputSchemaJson(
                resolvedCapability,
                capabilityBinding,
                includeInputSchema,
            );
        }

        setDefaults({ description, visibility, inputSchemaJson });
        setIsUpdateMode(effectiveUpdateMode);
        setEnableAfterUpdate(enableAfterCapabilityUpdate);
        setIsOpen(true);
    };

    return {
        isOpen,
        isUpdateMode,
        enableAfterUpdate,
        defaults,
        open,
        close: () => setIsOpen(false),
        onOpenChange: setIsOpen,
    };
}

function validateInputSchemaJson(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) return 'Input schema is required';

    try {
        const parsed = JSON.parse(trimmed);
        if (!Array.isArray(parsed)) return 'Input schema must be a JSON array';

        for (const [index, item] of parsed.entries()) {
            if (!item || typeof item !== 'object' || Array.isArray(item)) {
                return `Input schema item at index ${index} must be an object`;
            }
            if (typeof (item as { name?: unknown }).name !== 'string' || !(item as { name: string }).name.trim()) {
                return `Input schema item at index ${index} must include a non-empty "name"`;
            }
        }
        return null;
    } catch {
        return 'Input schema must be valid JSON';
    }
}

export function McpToolEditorModal({
    isOpen,
    defaultDescription,
    defaultVisibility = 'internal',
    defaultInputSchemaJson = '',
    showInputSchema = false,
    descriptionHelpText,
    inputSchemaPlaceholder,
    isUpdateMode = false,
    isLoading = false,
    onOpenChange,
    onConfirm,
}: McpToolEditorModalProps) {
    const { t } = useTranslation();
    const [description, setDescription] = useState(defaultDescription);
    const [visibility, setVisibility] = useState<McpToolVisibility>(defaultVisibility);
    const [inputSchemaJson, setInputSchemaJson] = useState(defaultInputSchemaJson);
    const [descriptionError, setDescriptionError] = useState<string | null>(null);
    const [inputSchemaError, setInputSchemaError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setDescription(defaultDescription);
        setVisibility(normalizeMcpToolVisibility(defaultVisibility));
        setInputSchemaJson(defaultInputSchemaJson);
        setDescriptionError(null);
        setInputSchemaError(null);
    }, [isOpen, defaultDescription, defaultVisibility, defaultInputSchemaJson]);

    const handleConfirm = () => {
        const trimmedDescription = description.trim();
        if (!trimmedDescription) {
            setDescriptionError(t('Description is required'));
            return;
        }

        if (showInputSchema) {
            const schemaError = validateInputSchemaJson(inputSchemaJson);
            if (schemaError) {
                setInputSchemaError(t(schemaError));
                return;
            }
        }

        setDescriptionError(null);
        setInputSchemaError(null);
        onConfirm({
            description: trimmedDescription,
            visibility,
            ...(showInputSchema ? { inputSchemaJson: inputSchemaJson.trim() } : {}),
        });
    };

    return (
        <Modal isOpen={isOpen} onOpenChange={onOpenChange} placement="center" backdrop="blur" size="2xl">
            <ModalContent>
                {(onClose) => (
                    <>
                        <ModalHeader className="flex flex-col gap-1">
                            {t(isUpdateMode ? 'Update MCP Tool' : 'Add description to enable MCP tool')}
                        </ModalHeader>
                        <ModalBody className="gap-4">
                            <Textarea
                                label={t('Description')}
                                labelPlacement="outside"
                                isRequired
                                variant="bordered"
                                minRows={3}
                                value={description}
                                onValueChange={(value) => {
                                    setDescription(value);
                                    if (descriptionError && value.trim()) setDescriptionError(null);
                                }}
                                isInvalid={Boolean(descriptionError)}
                                errorMessage={descriptionError ?? undefined}
                                placeholder={t('Describe what this MCP tool does')}
                            />
                            {descriptionHelpText && (
                                <p className="text-sm text-default-500">{t(descriptionHelpText)}</p>
                            )}
                            {showInputSchema && (
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-default-500 mb-1">
                                        {t('Body')}
                                    </p>
                                    <p className="text-sm text-default-500 mb-2">
                                        {t('Prepare JSON input schema for this tool. This will be passed as inputSchema when creating the MCP tool.')}
                                    </p>
                                    <Textarea
                                        aria-label={t('Body')}
                                        variant="bordered"
                                        minRows={6}
                                        value={inputSchemaJson}
                                        onValueChange={(value) => {
                                            setInputSchemaJson(value);
                                            if (inputSchemaError) setInputSchemaError(null);
                                        }}
                                        isInvalid={Boolean(inputSchemaError)}
                                        errorMessage={inputSchemaError ?? undefined}
                                        placeholder={inputSchemaPlaceholder}
                                        classNames={{ input: 'font-mono text-sm' }}
                                    />
                                </div>
                            )}
                            <Select
                                label={t('Visibility')}
                                labelPlacement="outside"
                                variant="bordered"
                                selectedKeys={[visibility]}
                                onSelectionChange={(keys) => {
                                    const selectedVisibility = Array.from(keys)[0];
                                    if (selectedVisibility) {
                                        setVisibility(String(selectedVisibility) as McpToolVisibility);
                                    }
                                }}
                            >
                                <SelectItem key="internal" value="internal">{t('Internal')}</SelectItem>
                                <SelectItem key="external" value="external">{t('External')}</SelectItem>
                            </Select>
                        </ModalBody>
                        <ModalFooter>
                            <CtyButton variant="light" onPress={onClose} isDisabled={isLoading}>
                                {t('Cancel')}
                            </CtyButton>
                            <CtyButton color="primary" onPress={handleConfirm} isLoading={isLoading}>
                                {t(isUpdateMode ? 'Update' : 'Enable MCP Tool')}
                            </CtyButton>
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
}
